import { IdPrefix } from '@monetize/types/common';
import { ApiDataMapKey } from '../api/queryUtils';
import {
  ACCOUNTS_AND_QUOTES_ROLES,
  ACCOUNTS_ROLES,
  PROD_CATALOG_ROLES,
} from './roles';

export const EMPTY_ARRAY = [];

export const amountFilter = [
  { id: 1, label: '$0 - $19,999', bt: '0,20000' },
  {
    id: 2,
    label: '$20,000 - $39,999',
    bt: '20000,40000',
  },
  {
    id: 3,
    label: '$40,000 - $59,999',
    bt: '40000,60000',
  },
  {
    id: 4,
    label: '$60,000 - $79,999',
    bt: '60000,80000',
  },
  {
    id: 5,
    label: '$80,000 - $99,999',
    bt: '80000,100000',
  },
  { id: 6, label: '$100,000+', gte: 100000 },
];

// for MCustomNumberInput
export const intNumRegex = /^\d*$/;
export const intNumWithNegativeRegex = /^[-]?\d*$/;
export const decimalRegex = /^\d+\.?\d*$/;
export const decimalWithNegativeRegex = /^[-]?\d+\.?\d*$/;
// when addition is enabled
export const hasAdditionRegex = /^[0-9]*\++[0-9]*$/;
export const additionRegex = /^\+?[0-9]+$/;
export const specialCharacterRegex = /[^\w\s]/gi;
export const SAFE_FILENAME = /[/?<>\\:*|"]/g;
export const NOT_NUMERIC = /[^0-9]/g;

export const ISO_COMPATIBLE_DATE =
  /[0-9]{4}-[0-9]{2}-[0-9]{2}($|T[0-9]{2}| [0-9]{2}(\+[0-9]{2}:[0-9]{2}|z|-[0-9]{4}|:[0-9]{2}:[0-9]{2}\.[0-9]{3}\+[0-9]{2}($|:[0-9]{2})|$|:[0-9]{2}($|:[0-9]{2}($|.[0-9]{3}))))/;

export const TWENTY_FOUR_HR_TIME = /^[0-9]{1,2}:[0-9]{1,2}:[0-9]{1,2}$/;
export const TWELVE_HR_TIME = /^[0-9]{1,2}:[0-9]{1,2} (A|P)M$/;

export enum DateFormatEnum {
  MM_DD_YYYY = 'MM_DD_YYYY',
  DD_MM_YYYY = 'DD_MM_YYYY',
  YYYY_MM_DD = 'YYYY_MM_DD',
}

export type IdPrefixValue = {
  apiKey: ApiDataMapKey;
  entityName: string;
  nameField: string;
  plural?: string;
  roles?: string[];
};
export type IdPrefixConfigType = Record<IdPrefix, IdPrefixValue>;

// Map ID prefixes to their corresponding API keys and display names
export const ID_PREFIX_TO_CONFIG: IdPrefixConfigType = {
  acct: {
    apiKey: 'accountSearch',
    entityName: 'Account',
    nameField: 'accountName',
    roles: ACCOUNTS_ROLES,
  },
  acdoc: {
    apiKey: 'accountDocuments',
    entityName: 'Account Documents',
    nameField: 'name',
    roles: ACCOUNTS_ROLES,
  },
  crdt: {
    apiKey: 'accountCredits',
    entityName: 'Credit',
    nameField: 'name',
    roles: ACCOUNTS_ROLES,
  },
  crnt: {
    apiKey: 'creditNoteService',
    entityName: 'Credit Note',
    nameField: 'description',
    roles: ACCOUNTS_ROLES,
  },
  ctrct: {
    apiKey: 'contracts',
    entityName: 'Contract',
    nameField: 'startDate',
    roles: ACCOUNTS_AND_QUOTES_ROLES,
  },
  disc: {
    apiKey: 'productCatalogDiscounts',
    entityName: 'Discount',
    nameField: 'name',
    roles: PROD_CATALOG_ROLES,
  },
  disct: {
    apiKey: 'productCatalogDiscounts',
    entityName: 'Discount',
    nameField: 'name',
    roles: PROD_CATALOG_ROLES,
  },
  invc: {
    apiKey: 'invoices',
    entityName: 'Invoice',
    nameField: 'invoiceNumber',
    roles: ACCOUNTS_ROLES,
  },
  invce: {
    // Legacy prefix - 'invc' is being used instead
    apiKey: 'invoices',
    entityName: 'Invoice',
    nameField: 'invoiceNumber',
    roles: ACCOUNTS_ROLES,
  },
  offer: {
    apiKey: 'productCatalogOfferingsV2',
    entityName: 'Offering',
    nameField: 'name',
    roles: PROD_CATALOG_ROLES,
  },
  offr: {
    apiKey: 'productCatalogOfferingsV2',
    entityName: 'Offering',
    nameField: 'name',
    roles: PROD_CATALOG_ROLES,
  },
  opp: {
    apiKey: 'cpqOpportunities',
    entityName: 'Opportunity',
    nameField: 'name',
    plural: 'Opportunities',
    roles: ACCOUNTS_AND_QUOTES_ROLES,
  },
  prod: {
    apiKey: 'productCatalogProducts',
    entityName: 'Product',
    nameField: 'name',
    roles: PROD_CATALOG_ROLES,
  },
  pymt: {
    apiKey: 'payments',
    entityName: 'Payment',
    nameField: 'description',
    roles: ACCOUNTS_ROLES,
  },
  quot: {
    apiKey: 'cpqServiceQuotes',
    entityName: 'Quote',
    nameField: 'description',
    roles: ACCOUNTS_AND_QUOTES_ROLES,
  },
  rfnd: {
    apiKey: 'refunds',
    entityName: 'Refund',
    nameField: 'name',
    roles: ACCOUNTS_ROLES,
  },
  usgt: {
    apiKey: 'productCatalogUsageTypes',
    entityName: 'Usage Type',
    nameField: 'name',
    roles: PROD_CATALOG_ROLES,
  },
  pmmd: {
    apiKey: 'accountPaymentMethods',
    entityName: 'Payment Method',
    nameField: 'bankName',
    roles: ACCOUNTS_ROLES,
  },
  subs: {
    apiKey: 'accountSubscriptions',
    entityName: 'Subscription',
    nameField: 'accountName',
    roles: ACCOUNTS_ROLES,
  },
  bilg: {
    apiKey: 'billGroups',
    entityName: 'Billing',
    nameField: 'name',
    roles: ACCOUNTS_ROLES,
  },
  cont: {
    apiKey: 'contacts',
    entityName: 'Contact',
    nameField: 'fullName',
    roles: ACCOUNTS_ROLES,
  },
};
